/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ShareStoryModal.jsx — Partage d'une réussite en story (réseaux sociaux)
 * ======================================================================
 * Récupère les réussites partageables (faits réels), laisse l'agent choisir un
 * template, puis génère une image 9:16 (html2canvas) à télécharger ou partager
 * (Web Share API, idéal mobile / Instagram / Facebook).
 */
import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Download, Share2, Loader2, Sparkles } from 'lucide-react';
import StoryCard from './StoryCard';

const TYPE_LABEL = {
    sale: 'Vente',
    satisfaction: 'Satisfaction',
    review: 'Avis vendeur',
    top_performer: 'Top performer',
    recognition: 'Reconnaissance',
};

export default function ShareStoryModal({ open, onOpenChange }) {
    const [cards, setCards] = useState([]);
    const [firstName, setFirstName] = useState('');
    const [selected, setSelected] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        (async () => {
            try {
                const r = await api.get('/engagement/shareables');
                setCards(r.data.cards || []);
                setFirstName(r.data.agent_first_name || '');
                setSelected(0);
            } catch (e) {
                // silencieux
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    const capture = async () => {
        const node = cardRef.current;
        if (!node) return null;
        const canvas = await html2canvas(node, {
            scale: 3,
            backgroundColor: '#0B0712',
            useCORS: true,
            logging: false,
        });
        return new Promise((res) => canvas.toBlob((b) => res(b), 'image/png', 0.95));
    };

    const handleDownload = async () => {
        setBusy(true);
        try {
            const blob = await capture();
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'homly-reussite.png';
            link.click();
            URL.revokeObjectURL(url);
            toast.success('Image téléchargée — prête pour votre story !');
        } catch (e) {
            toast.error("La génération de l'image a échoué.");
        } finally {
            setBusy(false);
        }
    };

    const handleShare = async () => {
        setBusy(true);
        try {
            const blob = await capture();
            if (!blob) return;
            const file = new File([blob], 'homly-reussite.png', { type: 'image/png' });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: 'Ma réussite', text: 'Propulsé par Homly' });
            } else {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'homly-reussite.png';
                link.click();
                URL.revokeObjectURL(url);
                toast.success('Image téléchargée — partagez-la sur votre story !');
            }
        } catch (e) {
            // l'utilisateur a annulé le partage : pas d'erreur
        } finally {
            setBusy(false);
        }
    };

    const current = cards[selected];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-slate-950 border-slate-800 text-white max-w-md" data-testid="share-story-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-[#FF914D]" />
                        Partager une réussite
                    </DialogTitle>
                    <DialogDescription className="text-xs text-white/50">
                        Générez une image prête à partager en story sur vos réseaux (anonymisée).
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="h-[420px] flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-[#A855F7]" />
                    </div>
                ) : cards.length === 0 ? (
                    <div className="h-[300px] flex flex-col items-center justify-center text-center px-6" data-testid="share-story-empty">
                        <Sparkles className="w-8 h-8 text-[#A855F7] mb-3" />
                        <p className="text-sm text-white/80 font-medium">Vos premières réussites arrivent bientôt</p>
                        <p className="text-xs text-white/50 mt-1.5">
                            Concluez une vente ou recueillez un avis vendeur, et votre story sera prête à partager.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Sélecteur de template */}
                        <div className="flex flex-wrap gap-2">
                            {cards.map((c, i) => (
                                <button
                                    key={c.type}
                                    onClick={() => setSelected(i)}
                                    data-testid={`story-template-${c.type}`}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                        i === selected
                                            ? 'bg-[#681793] border-[#681793] text-white'
                                            : 'bg-transparent border-slate-700 text-slate-300 hover:border-slate-500'
                                    }`}
                                >
                                    {TYPE_LABEL[c.type] || c.type}
                                </button>
                            ))}
                        </div>

                        {/* Aperçu de la story */}
                        <div className="flex justify-center">
                            <div style={{ width: 360 }}>
                                <StoryCard ref={cardRef} card={current} agentName={firstName} />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                            <Button
                                onClick={handleDownload}
                                disabled={busy}
                                variant="outline"
                                className="flex-1 border-slate-700 text-white hover:bg-slate-800"
                                data-testid="story-download-btn"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                                Télécharger
                            </Button>
                            <Button
                                onClick={handleShare}
                                disabled={busy}
                                className="flex-1 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white hover:opacity-90"
                                data-testid="story-share-btn"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                Partager
                            </Button>
                        </div>
                        <p className="text-[11px] text-center text-white/40">
                            Idéal pour vos stories Instagram & Facebook · anonymisé
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
